import { buildConfluentAllStarCaseName } from "@/configuration/groups/confluent-all-star/case-name";
import { CONFLUENT_MISSING_PHONE_NOTE_ID } from "@/configuration/groups/confluent-all-star/chart-note-ids";
import {
  DecisionMetadata,
  isRetryingStep,
  outputId,
  recordOrigin,
  ReferralWorkflowTimeline,
  stepStatus,
  type ReferralWorkflowStep,
} from "@/configuration/groups/confluent-all-star/components/referral-timeline";
import { CONFLUENT_ALL_STAR_TIMEZONE } from "@/configuration/groups/confluent-all-star/configuration";
import { isConfluentNoneListedPayer } from "@/configuration/groups/confluent-all-star/insurance-payer-ipa";
import {
  getConfluentReferralStepKeys,
  type ConfluentReferralStepKey,
} from "@/configuration/groups/confluent-all-star/workflows/step-order";
import { FieldLayout, type Field } from "@/src/components/cards/common";
import { DiagnosisListTable } from "@/src/components/cards/diagnosis-list-table";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { ReferralExportResult } from "@/src/components/tasks/referral-export-result";
import { WorkflowStepInformationNeeded } from "@/src/components/tasks/workflow-step-information-needed";
import { WorkflowStepResumeButton } from "@/src/components/tasks/workflow-step-resume-button";
import { formatDateMmDdYyyy, getCurrentDateTimeInTimezone } from "@/src/lib/utils/date-and-phone-utils";
import {
  REFERRAL_INELIGIBLE_NOTE_ID,
  REFERRAL_UPLOAD_NOTE_ID,
} from "@/src/lib/workflows/steps/ehr/clinicient-chart-note-ids";
import { StepKey } from "@/src/lib/workflows/steps/types";
import type { Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { TaskStatus, type Task } from "@/src/models/task";
import type { TaskApproval } from "@/src/models/task-approval";
import { TaskStepStatus, type TaskStep } from "@/src/models/task-step";

type WrittenChartNote = { id: string; noteEhrId: string; note: string };

function chartNoteById(step: TaskStep | undefined, id: string): WrittenChartNote | null {
  const notes = step?.output?.notes;

  if (!Array.isArray(notes)) return null;

  return (
    notes.find(
      (note): note is WrittenChartNote =>
        typeof note?.id === "string" &&
        note.id === id &&
        typeof note?.noteEhrId === "string" &&
        typeof note?.note === "string"
    ) ?? null
  );
}

function formatReferringProvider(provider: Referral["referringProvider"]): string | null {
  if (!provider) return null;
  const name = [provider.givenName, provider.familyName].filter(Boolean).join(" ").trim();
  const npi = provider.npiNumber ? `NPI ${provider.npiNumber}` : null;

  return [name || null, npi].filter(Boolean).join(" · ") || null;
}

export function ConfluentReferralSteps({
  task,
  taskSteps,
  referral,
  pendingApproval,
}: {
  task: Task;
  taskSteps: TaskStep[];
  referral: Referral;
  pendingApproval: TaskApproval | null;
}) {
  const orderedTaskSteps = taskSteps.toSorted((a, b) => a.stepIndex - b.stepIndex);
  const findStep = (key: StepKey) => orderedTaskSteps.find((step) => step.stepKey === key);
  const patientStep = findStep(StepKey.ENSURE_PATIENT);
  const inheritanceStep = findStep(StepKey.CLINICIENT_SAME_DAY_CASE_INHERITANCE);
  const inheritedCaseId = outputId(inheritanceStep, "inheritedCaseEhrId");
  const inherited = inheritanceStep?.output?.inherited === true;
  const faxStep = findStep(StepKey.REQUIRE_PATIENT_PHONE);
  const faxSkipped = faxStep?.output?.outcome === "skipped";
  const faxId = outputId(faxStep, "faxId");
  const caseStep = findStep(StepKey.CLINICIENT_ENSURE_CASE);
  const configureStep = findStep(StepKey.CLINICIENT_CONFIGURE_CASE);
  const uploadStep = findStep(StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD);
  const noteStep = findStep(StepKey.CLINICIENT_DOCUMENT_NOTE);
  const insuranceStep = findStep(StepKey.CLINICIENT_SYNC_INSURANCE);
  const authorizationStep = findStep(StepKey.CLINICIENT_ENSURE_AUTHORIZATION);
  const authorizationUploadStep = findStep(StepKey.CLINICIENT_AUTHORIZATION_DOCUMENT_UPLOAD);
  const authorizationTaskStep = findStep(StepKey.CLINICIENT_AUTHORIZATION_TASK);
  const authorizationId = outputId(authorizationStep, "authorizationEhrId");
  const authorizationDocumentId = outputId(authorizationUploadStep, "documentEhrId");
  const authorizationTaskId = outputId(authorizationTaskStep, "taskEhrId");
  const authorizationSkipped = authorizationStep?.output?.outcome === "skipped";
  const authorizationUploadSkipped = authorizationUploadStep?.output?.outcome === "skipped";
  const authorizationUploadRejected = authorizationUploadStep?.output?.outcome === "request_rejected";
  const authorizationTaskSkipped = authorizationTaskStep?.output?.outcome === "skipped";
  const exportStep = findStep(StepKey.REFERRAL_SHEET_EXPORT);
  const exportRange = outputId(exportStep, "updatedRange");
  const exportSkippedForExistingAppointment = exportRange === "skipped:existing-appointment";
  const exportRetrying = isRetryingStep(exportStep);
  const exportFailed = exportStep?.status === TaskStepStatus.FAILED && !exportRetrying;
  const patientId = outputId(patientStep, "patientEhrId");
  const caseId = outputId(caseStep, "patientCaseEhrId");
  const documentId = outputId(uploadStep, "documentEhrId");
  const uploadRejected = uploadStep?.output?.outcome === "request_rejected";
  const uploadNeedsReport = uploadRejected || uploadStep?.status === TaskStepStatus.FAILED;
  const authorizationUploadNeedsReport =
    authorizationUploadRejected || authorizationUploadStep?.status === TaskStepStatus.FAILED;
  const noteSkipped = noteStep?.output?.outcome === "request_rejected";
  const uploadNote = chartNoteById(noteStep, REFERRAL_UPLOAD_NOTE_ID);
  const noteId = uploadNote?.noteEhrId ?? null;
  const ineligibleNote = chartNoteById(noteStep, REFERRAL_INELIGIBLE_NOTE_ID);
  const missingPhoneNote = chartNoteById(noteStep, CONFLUENT_MISSING_PHONE_NOTE_ID);
  const insurancePlanId = outputId(insuranceStep, "insurancePlanId");
  const existingCase = caseStep?.output?.created === false;
  const insuranceSkipped = insuranceStep?.output?.outcome === "skipped";
  const lastStep = orderedTaskSteps.at(-1);
  const onHold = task.status === TaskStatus.AWAITING_INFORMATION || task.status === TaskStatus.REVIEW_NEEDED;
  // Configure-case has no success output, so its final completed row can also represent an early exit.
  const configureHeld = onHold && configureStep !== undefined && configureStep.id === lastStep?.id;

  // Intake-only fields live on the reviewed section. Persisted referral fields match the sync's source.
  const reviewedContent = (referral.section?.reviewedContent ?? {}) as Record<string, unknown>;
  const reviewedString = (key: string): string | undefined => {
    const value = reviewedContent[key];

    return typeof value === "string" && value.trim() ? value : undefined;
  };

  const reviewedBoolean = (key: string): boolean | undefined =>
    typeof reviewedContent[key] === "boolean" ? (reviewedContent[key] as boolean) : undefined;
  const priorAuthorization = task.priorAuthorization;

  const bodyPart = reviewedString("bodyPart") ?? null;
  const serviceLine = referral.serviceLine;
  const dateOfOnset = reviewedString("dateOfOnset") ?? null;
  const reasonForReferral = referral.reasonForReferral;
  // Legacy reviewed content may still key the primary payer as `ehrPayerName` before the rename.
  const primaryEhrPayerName = reviewedString("primaryEhrPayerName") ?? reviewedString("ehrPayerName") ?? null;
  const secondaryEhrPayerName = reviewedString("secondaryEhrPayerName") ?? null;
  const hasSecondaryInsurance =
    Boolean(referral.secondaryCoverage) ||
    (secondaryEhrPayerName !== null && !isConfluentNoneListedPayer(secondaryEhrPayerName));
  const isWorkersComp = reviewedBoolean("isWorkersComp") ?? false;
  const isPostOpReferral = reviewedBoolean("isPostOpReferral") ?? null;
  const isPostOpPriorAuth = reviewedBoolean("isPostOpPriorAuth") ?? priorAuthorization?.isPostOpPriorAuth ?? null;
  const isContinuationOfCare = reviewedBoolean("isContinuationOfCare") === true;
  const numberOfVisits = referral.numberOfVisits;
  const authorizationNumber = referral.authorizationNumber ?? priorAuthorization?.authorizationNumber ?? null;
  const authorizationStart = reviewedString("startDate") ?? priorAuthorization?.startDate ?? null;
  const authorizationEnd = reviewedString("endDate") ?? priorAuthorization?.endDate ?? null;
  const diagnoses =
    referral.diagnosisList ??
    (referral.diagnosisCode || referral.diagnosisText
      ? [
          {
            diagnosisCode: referral.diagnosisCode,
            diagnosisCodeType: referral.diagnosisCodeType ?? null,
            diagnosisText: referral.diagnosisText,
          },
        ]
      : []);

  const patient = referral.patient;
  // Generated case name, used as a fallback when the synced case name has not been recorded yet.
  const generatedCaseName = buildConfluentAllStarCaseName({
    diagnosisList: diagnoses.length ? diagnoses : null,
    reasonForReferral,
    bodyPart,
    primaryEhrPayerName,
    serviceLine,
    dateOfBirth: patient.dateOfBirth,
    isPostOpReferral,
    isPostOpPriorAuth,
  });
  // The case's actual name from the ensure-case step output (the matched case's real description when
  // reused, or the generated name when created), falling back to the generated name if not recorded.
  const syncedCaseName = outputId(caseStep, "caseName") ?? generatedCaseName;
  // Mirrors the description the upload step applies (referral-document-input.ts).
  const referralDate = formatDateMmDdYyyy(referral.dateOfReferral);
  const documentDescription = referralDate ? `${referralDate} Referral` : "Referral";
  const authorizationTaskAction = isContinuationOfCare ? "Re-Authorization" : "Authorization";
  // The created task's due date: current date + 1 day, in the clinic's timezone.
  const authorizationTaskDueDate = getCurrentDateTimeInTimezone(CONFLUENT_ALL_STAR_TIMEZONE)
    .plus({ days: 1 })
    .toFormat("MM/dd/yyyy");

  const caseIntakeFields: Field[] = [
    { key: "caseName", label: "Case Name", getValue: () => syncedCaseName },
    { key: "bodyPart", label: "Body Part", getValue: () => bodyPart },
    { key: "dateOfOnset", label: "Date of Onset", type: "date", getValue: () => dateOfOnset },
    {
      key: "reasonForReferral",
      label: "Reason for Referral",
      colSpan: 2,
      getValue: () => reasonForReferral,
    },
    {
      key: "referringProvider",
      label: "Referring Provider",
      getValue: () => formatReferringProvider(referral.referringProvider),
    },
  ];
  const isPostOp = isPostOpReferral === true || isPostOpPriorAuth === true;
  const caseDetailFields: Field[] = [
    { key: "dateOfOnset", label: "Date of Onset", type: "date", getValue: () => dateOfOnset },
    {
      key: "surgicalDate",
      label: "Surgical Date",
      type: "date",
      hideIfEmpty: true,
      getValue: () => (isPostOp ? dateOfOnset : null),
    },
    { key: "serviceLine", label: "Service Line", getValue: () => serviceLine },
    { key: "facility", label: "Facility", getValue: () => referral.receivingLocation?.name ?? null },
    { key: "isWorkersComp", label: "Workers' Comp", getValue: () => (isWorkersComp ? "Yes" : "No") },
    {
      key: "referringProvider",
      label: "Referring Provider",
      getValue: () => formatReferringProvider(referral.referringProvider),
    },
  ];
  const primaryInsuranceFields: Field[] = [
    {
      key: "referringLocation",
      label: "Referring Provider Location",
      colSpan: 3,
      getValue: () => referral.referringLocation?.name ?? null,
    },
    { key: "primaryEhrPayerName", label: "Clinicient Insurance Name", getValue: () => primaryEhrPayerName },
    {
      key: "primarySubscriber",
      label: "Subscriber Number",
      getValue: () => referral.primaryCoverage?.subscriberId ?? null,
    },
    { key: "primaryGroup", label: "Group Number", getValue: () => referral.primaryCoverage?.groupNumber ?? null },
  ];
  const secondaryInsuranceFields: Field[] = [
    { key: "secondaryEhrPayerName", label: "Clinicient Insurance Name", getValue: () => secondaryEhrPayerName },
    {
      key: "secondarySubscriber",
      label: "Subscriber Number",
      getValue: () => referral.secondaryCoverage?.subscriberId ?? null,
    },
    { key: "secondaryGroup", label: "Group Number", getValue: () => referral.secondaryCoverage?.groupNumber ?? null },
  ];
  const authorizationFields: Field[] = [
    { key: "authorizationNumber", label: "Authorization Number", getValue: () => authorizationNumber },
    { key: "authorizationStart", label: "Start Date", type: "date", getValue: () => authorizationStart },
    { key: "authorizationEnd", label: "End Date", type: "date", getValue: () => authorizationEnd },
    {
      key: "numberOfVisits",
      label: "Number of Visits",
      getValue: () => (numberOfVisits != null ? String(numberOfVisits) : null),
    },
  ];
  const authorizationTaskFields: Field[] = [
    { key: "action", label: "Action", getValue: () => authorizationTaskAction },
    { key: "dueDate", label: "Due Date", getValue: () => authorizationTaskDueDate },
    { key: "priority", label: "Priority", getValue: () => "Medium" },
    { key: "role", label: "Assign to Role", getValue: () => "Front Desk" },
    { key: "case", label: "Case", getValue: () => syncedCaseName },
  ];
  const diagnosisFallback = referral.diagnosisCode
    ? { diagnosisCode: referral.diagnosisCode, diagnosisText: referral.diagnosisText }
    : null;

  const definitions: Record<ConfluentReferralStepKey, ReferralWorkflowStep> = {
    [StepKey.CLINICIENT_SAME_DAY_PHONE_INHERITANCE]: {
      title: "Hold for a same-day phone number (retired)",
      status: stepStatus(
        findStep(StepKey.CLINICIENT_SAME_DAY_PHONE_INHERITANCE),
        findStep(StepKey.CLINICIENT_SAME_DAY_PHONE_INHERITANCE)?.status === TaskStepStatus.COMPLETED
      ),
      summary: "This step no longer holds referrals; it only remains on referrals created while it was active.",
      content: null,
    },
    [StepKey.REQUIRE_PATIENT_PHONE]: {
      title: "Check required patient information",
      status: stepStatus(faxStep, faxId !== null || faxSkipped, onHold && faxStep?.id === lastStep?.id, faxSkipped),
      summary: faxId
        ? "The patient is missing a phone number. A fax has been sent to the sender requesting it. No action is needed here; the referral continues to be processed."
        : faxSkipped
          ? "The patient has all required information."
          : "The patient information check has not completed yet.",
      content: (
        <DecisionMetadata
          items={[
            { label: "Fax recipient", value: outputId(faxStep, "targetFaxNumber") ?? "Not recorded" },
            { label: "Fax record", value: faxId ?? "Not recorded" },
          ]}
        />
      ),
    },
    [StepKey.ENSURE_PATIENT]: {
      title: "Confirm patient chart exists in Clinicient with the following demographic information",
      status: stepStatus(patientStep, patientId !== null),
      summary:
        "For existing patients, only mobile phone number must be updated to reflect the information shown here. For new patients, ensure all information below is included in the chart.",
      content: (
        <div className="space-y-3">
          <PatientCard patient={patient} />
          <DecisionMetadata
            items={[
              { label: "Clinicient patient ID", value: patientId ?? "Not recorded" },
              { label: "Patient", value: recordOrigin(patientStep?.output?.created) },
            ]}
          />
        </div>
      ),
    },
    [StepKey.CLINICIENT_SAME_DAY_CASE_INHERITANCE]: {
      title: "Inherit case from a same-day referral",
      status: stepStatus(inheritanceStep, typeof inheritanceStep?.output?.inherited === "boolean"),
      summary: inherited
        ? "This referral had no diagnosis details, so it adopted the Clinicient case and diagnosis of another referral received the same day for this patient."
        : "This step only applies when a referral has no diagnosis details and another referral received the same day already has a case. Otherwise the referral is matched or created normally in the next step.",
      content: (
        <DecisionMetadata
          items={[
            {
              label: "Outcome",
              value: inherited ? "Inherited a same-day referral's case" : "Not applicable",
            },
            { label: "Inherited case", value: inheritedCaseId ?? "Not recorded" },
          ]}
        />
      ),
    },
    [StepKey.CLINICIENT_ENSURE_CASE]: {
      title: "Find or create the referral case",
      status: stepStatus(caseStep, caseId !== null),
      summary:
        "Match the referral to an existing Clinicient case or create a new case when appropriate. Ambiguous matches require review.",
      content: (
        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium">All of the following must be true for a case to match the incoming referral:</p>
            <ol className="mt-1 list-decimal space-y-1 pl-5 text-muted-foreground">
              <li>The case is Active.</li>
              <li>
                The patient has had an appointment for the case within the past 90 days or has one upcoming in the next
                90 days, or the case was created within the past 30 days.
              </li>
              <li>
                <code>serviceLine</code> from intake matches the Discipline in Clinicient (i.e. both Physical Therapy).
              </li>
              <li>
                Body part, diagnosis, and reason for referral are similar to the body part in the existing case name and
                the ICD-10 codes listed on the case.
              </li>
            </ol>
            <p className="mt-1 text-muted-foreground">
              If multiple cases match, choose the one with the more recent associated appointment.
            </p>
          </div>
          <FieldLayout hideEmptyFields={false} fields={caseIntakeFields} patient={referral} />
          <div className="space-y-1">
            <p className="font-medium">ICD-10 codes</p>
            <DiagnosisListTable diagnoses={diagnoses} fallbackDiagnosis={diagnosisFallback} />
          </div>
          <DecisionMetadata
            items={[
              { label: "Clinicient case ID", value: caseId ?? "Not recorded" },
              {
                label: "Case",
                value: recordOrigin(caseStep?.output?.created),
              },
            ]}
          />
        </div>
      ),
    },
    [StepKey.CLINICIENT_CONFIGURE_CASE]: {
      title: "Add case details",
      status: stepStatus(configureStep, caseId !== null, configureHeld, existingCase),
      summary: existingCase
        ? "Preserve the matched case’s existing configuration."
        : 'Ensure that all details below are added in the Case Details, Referral, and Diagnosis Codes sections of the case. Also ensure that "Authorized to Received Payment" is selected and that the "Work Related" checkbox is selected if the referral is from worker\'s comp. Add company if needed. Add the Date of Onset if listed, and if the condition is Post-Op, also add the Date of Onset to the Surgical Date field.',
      content: existingCase ? (
        <p className="text-sm">Verify the case details in Clinicient before completing the task.</p>
      ) : (
        <div className="space-y-3">
          <FieldLayout hideEmptyFields={false} fields={caseDetailFields} patient={referral} />
          <div className="space-y-1">
            <p className="text-sm font-medium">Diagnosis codes</p>
            <DiagnosisListTable diagnoses={diagnoses} fallbackDiagnosis={diagnosisFallback} />
          </div>
        </div>
      ),
    },
    [StepKey.CLINICIENT_CASE_DOCUMENT_UPLOAD]: {
      title: "Upload the referral document as a case record",
      status: stepStatus(uploadStep, documentId !== null),
      summary: uploadRejected
        ? "The document upload was rejected. The workflow continued."
        : "Upload the referral document to the case as a Case Record attachment.",
      content: (
        <div className="space-y-3 text-sm">
          <p>In the Attachments section of the patient chart, upload the document with:</p>
          <dl className="space-y-1 [overflow-wrap:anywhere]">
            <div>
              <dt className="inline font-medium">Attachment Type: </dt>
              <dd className="inline">Case Record</dd>
            </div>
            <div>
              <dt className="inline font-medium">Case: </dt>
              <dd className="inline">{syncedCaseName}</dd>
            </div>
            <div>
              <dt className="inline font-medium">Description: </dt>
              <dd className="inline">{documentDescription}</dd>
            </div>
          </dl>
          <DecisionMetadata items={[{ label: "Clinicient document ID", value: documentId ?? "Not recorded" }]} />
        </div>
      ),
    },
    [StepKey.CLINICIENT_DOCUMENT_NOTE]: {
      title: "Add note(s) to patient chart",
      status: stepStatus(noteStep, noteSkipped || noteId !== null, false, noteSkipped),
      summary: noteSkipped
        ? "Skipped because the case document upload was not confirmed."
        : "To the Notes section of the patient chart, add the following note(s):",
      content: noteSkipped ? undefined : (
        <div className="space-y-3">
          <div className="rounded-md border bg-muted/30 px-3 py-2">
            <CopyableContent text="Referral document uploaded to attachments." alwaysShow />
          </div>
          <DecisionMetadata items={[{ label: "Clinicient note ID", value: noteId ?? "Not recorded" }]} />
          {ineligibleNote ? (
            <div className="space-y-2 border-t pt-3">
              <p className="text-sm font-medium">This referral is ineligible for scheduling — also add this note:</p>
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <CopyableContent text={ineligibleNote.note} alwaysShow />
              </div>
              <DecisionMetadata items={[{ label: "Clinicient note ID", value: ineligibleNote.noteEhrId }]} />
            </div>
          ) : null}
          {missingPhoneNote ? (
            <div className="space-y-2 border-t pt-3">
              <p className="text-sm font-medium">The referral has no phone number — also add this note:</p>
              <div className="rounded-md border bg-muted/30 px-3 py-2">
                <CopyableContent text={missingPhoneNote.note} alwaysShow />
              </div>
              <DecisionMetadata items={[{ label: "Clinicient note ID", value: missingPhoneNote.noteEhrId }]} />
            </div>
          ) : null}
        </div>
      ),
    },
    [StepKey.CLINICIENT_SYNC_INSURANCE]: {
      title: "Add primary insurance to case",
      status: stepStatus(
        insuranceStep,
        insuranceSkipped || (insuranceStep?.output?.outcome === "synced" && insurancePlanId !== null),
        false,
        insuranceSkipped
      ),
      summary: insuranceSkipped
        ? "Preserve the matched case’s existing insurance. Verify coverage in Clinicient."
        : insuranceStep?.output?.outcome === "waiting"
          ? "Waiting for another referral for this patient to supply primary insurance."
          : "Ensure that all of the following information has been added to the case. The subscriber is always configured as Self. Add secondary insurance if needed — secondary insurance must be added manually.",
      content: insuranceSkipped ? undefined : (
        <div className="space-y-3">
          <FieldLayout hideEmptyFields={false} fields={primaryInsuranceFields} patient={referral} />
          <DecisionMetadata
            items={[{ label: "Clinicient primary insurance plan ID", value: insurancePlanId ?? "Not recorded" }]}
          />
          {hasSecondaryInsurance ? (
            <div className="space-y-2">
              <p className="text-sm font-medium">Secondary insurance (add manually)</p>
              <FieldLayout hideEmptyFields={false} fields={secondaryInsuranceFields} patient={referral} />
            </div>
          ) : null}
        </div>
      ),
    },
    [StepKey.CLINICIENT_ENSURE_AUTHORIZATION]: {
      title: "Add authorization number to case, if needed.",
      status: stepStatus(
        authorizationStep,
        authorizationSkipped || authorizationId !== null,
        false,
        authorizationSkipped
      ),
      summary: authorizationSkipped
        ? "No authorization number was provided for this referral."
        : "If the incoming referral includes an authorization number, it must be added to the case (unless the same authorization is already listed in the case). If start and end date are not listed, use today's date for both. If number of visits is not listed, use 1 as a placeholder.",
      content: authorizationSkipped ? undefined : (
        <div className="space-y-3">
          <FieldLayout hideEmptyFields={false} fields={authorizationFields} patient={referral} />
          <DecisionMetadata
            items={[
              { label: "Clinicient authorization ID", value: authorizationId ?? "Not recorded" },
              { label: "Authorization", value: recordOrigin(authorizationStep?.output?.created) },
            ]}
          />
        </div>
      ),
    },
    [StepKey.CLINICIENT_AUTHORIZATION_DOCUMENT_UPLOAD]: {
      title: "Attach the referral document to the authorization",
      status: stepStatus(
        authorizationUploadStep,
        authorizationUploadSkipped || authorizationDocumentId !== null,
        false,
        authorizationUploadSkipped
      ),
      summary: authorizationUploadRejected
        ? "The authorization document upload was rejected. The workflow continued."
        : authorizationUploadSkipped
          ? "No authorization attachment is needed for this referral."
          : "Unless there is a matching authorization number with a document already attached (check by clicking the paperclip button next to the authorization information), the document must be uploaded as an attachment to the authorization by clicking this paperclip button.",
      content:
        authorizationUploadSkipped || authorizationUploadRejected ? undefined : (
          <DecisionMetadata
            items={[
              { label: "Clinicient authorization document ID", value: authorizationDocumentId ?? "Not recorded" },
              { label: "Attachment", value: recordOrigin(authorizationUploadStep?.output?.uploaded) },
            ]}
          />
        ),
    },
    [StepKey.CLINICIENT_AUTHORIZATION_TASK]: {
      title: "Create the authorization task",
      status: stepStatus(
        authorizationTaskStep,
        authorizationTaskSkipped || authorizationTaskId !== null,
        false,
        authorizationTaskSkipped
      ),
      summary: authorizationTaskSkipped
        ? "For newly-created cases or referral documents that do not contain an authorization number, no authorization task is required."
        : 'If the document is a Continuation of Care, create a "Re-Authorization" task. If the document is not, create an "Authorization" task.',
      content: authorizationTaskSkipped ? undefined : (
        <div className="space-y-3">
          <FieldLayout hideEmptyFields={false} fields={authorizationTaskFields} patient={referral} />
          <DecisionMetadata items={[{ label: "Clinicient task ID", value: authorizationTaskId ?? "Not recorded" }]} />
        </div>
      ),
    },
    [StepKey.REFERRAL_SHEET_EXPORT]: {
      title: "Export to the referral spreadsheet",
      status: stepStatus(exportStep, exportRange !== null, false, exportSkippedForExistingAppointment),
      summary: exportSkippedForExistingAppointment
        ? "Skipped spreadsheet export because the case already has an upcoming appointment within 90 days."
        : exportStep?.status === TaskStepStatus.COMPLETED
          ? "The referral row was exported and confirmed in the spreadsheet."
          : exportRetrying
            ? "The export hit a temporary error and will retry automatically."
            : exportFailed
              ? "Export failed. Fix the issue and retry before verifying this referral."
              : "The final automated step exports this referral to the tracking spreadsheet.",
      content: exportFailed ? (
        <ReferralExportResult key={task.updatedAt} task={task} />
      ) : exportRange && !exportSkippedForExistingAppointment ? (
        <DecisionMetadata items={[{ label: "Spreadsheet row", value: exportRange }]} />
      ) : undefined,
    },
  };
  const note = task.files.find((file) => file.type === FileType.INBOUND)?.note;
  const informationNeededStep = onHold ? lastStep : undefined;
  const stepKeys = getConfluentReferralStepKeys(orderedTaskSteps.map((step) => step.stepKey));
  const hasInformationNeededStep = stepKeys.some((key) => key === informationNeededStep?.stepKey);
  const steps: ReferralWorkflowStep[] = stepKeys.map((key) => {
    const step = definitions[key];
    const isBlockingStep = key === lastStep?.stepKey && (onHold || task.status === TaskStatus.FAILED);
    const needsAction = key === informationNeededStep?.stepKey;

    return {
      ...step,
      status: step.status === "failed" ? "failed" : needsAction ? "needs_action" : step.status,
      content: needsAction ? (
        <>
          <WorkflowStepInformationNeeded note={note} />
          {step.content}
        </>
      ) : (
        step.content
      ),
      continuation:
        isBlockingStep && !pendingApproval && lastStep && key !== StepKey.REFERRAL_SHEET_EXPORT
          ? {
              instruction: "After fixing the issue, continue from this step. Completed steps will be preserved.",
              control: (
                <WorkflowStepResumeButton key={`${lastStep.id}:${task.updatedAt}`} task={task} step={lastStep} />
              ),
            }
          : undefined,
    };
  });
  const allSynced = steps.every((step) => step.status === "automated" || step.status === "not_needed");
  const isCompleted = task.status === TaskStatus.COMPLETED;
  const isProcessing = task.status === TaskStatus.PROCESSING;
  const hasFailedStep = taskSteps.some((step) => step.status === TaskStepStatus.FAILED && !isRetryingStep(step));
  const hasRetryingStep = taskSteps.some(isRetryingStep);
  const hasTimedHold = taskSteps.some((step) => step.status === TaskStepStatus.PAUSED);
  const hasAsyncStep = taskSteps.some((step) => step.status === TaskStepStatus.ASYNC);
  const workflowNeedsAttention = onHold || hasFailedStep;
  const workflowContinuing = isProcessing || hasAsyncStep || hasTimedHold || hasRetryingStep;
  const canVerify =
    task.status === TaskStatus.VERIFICATION_NEEDED &&
    allSynced &&
    !pendingApproval &&
    !workflowNeedsAttention &&
    !hasAsyncStep &&
    !hasTimedHold;
  const needsAttention =
    workflowNeedsAttention ||
    Boolean(pendingApproval) ||
    steps.some((step) => step.status === "needs_action" || step.status === "failed");

  steps.push({
    title: "Verify in Clinicient",
    status: isCompleted
      ? "verified"
      : hasFailedStep
        ? "failed"
        : workflowNeedsAttention || pendingApproval || canVerify
          ? "needs_action"
          : "waiting",
    summary: isCompleted
      ? "This task has been marked complete."
      : pendingApproval
        ? "Complete the pending workflow confirmation before verifying this task."
        : workflowNeedsAttention
          ? "Resolve the outstanding workflow issue before verifying this task."
          : workflowContinuing
            ? "The workflow is continuing. Verify the results when it finishes."
            : !allSynced
              ? "Resolve the incomplete sync steps above before verifying this task."
              : "Confirm the patient, case, referral document, chart note, and insurance in Clinicient and complete the remaining manual work.",
    content: (
      <p className="text-sm">
        Verify the insurance and referral documents in Clinicient. Complete any remaining secondary insurance and
        authorization work, then use Correct or Incorrect at the bottom to complete this task.
      </p>
    ),
  });

  return (
    <ReferralWorkflowTimeline
      steps={steps}
      isProcessing={workflowContinuing}
      priorityContent={
        uploadNeedsReport || authorizationUploadNeedsReport ? (
          <div className="rounded-md border border-warn/30 bg-warn/10 px-3 py-3 text-sm">
            <p className="font-semibold">Report this referral to the Confluent team.</p>
            <p>
              Clinicient did not confirm the {uploadNeedsReport ? "case record upload" : ""}
              {uploadNeedsReport && authorizationUploadNeedsReport ? " or " : ""}
              {authorizationUploadNeedsReport ? "authorization attachment upload" : ""}. Include referral task{" "}
              {task.displayId ?? task.id}.
            </p>
          </div>
        ) : undefined
      }
      waitingMessage={
        !isCompleted && taskSteps.length === 0 && !needsAttention
          ? "No Clinicient sync steps have been recorded for this task yet."
          : undefined
      }
      summaryContent={
        <>
          <p className="text-sm text-muted-foreground">
            SMS eligibility is assessed on the Scheduling Eligibility Verification task after this referral is
            completed.
          </p>
          {needsAttention && note && !hasInformationNeededStep ? (
            <p className="whitespace-pre-wrap text-sm">{note}</p>
          ) : null}
        </>
      }
    />
  );
}
